import { Checkbox, Switch } from '@ff-it/ui';
import { Icon } from 'components/Icon';
import { useId, type FocusEventHandler } from 'react';
import { Tooltip } from 'react-tooltip';
import { autoFactorKinds, factorKindLabels, type FactorPolicyDraft } from './form';
import * as styles from './FactorPolicyEditor.css';

export type FactorPolicyEditorProps = {
  label: string;
  value: FactorPolicyDraft;
  onChange: (value: FactorPolicyDraft) => void;
  presenceLabel?: string;
  inheritHelp?: string;
  disabled?: boolean;
  readOnly?: boolean;
  disabledReason?: string;
  onBlur?: FocusEventHandler<HTMLFieldSetElement>;
  onFocus?: FocusEventHandler<HTMLFieldSetElement>;
};

export function FactorPolicyEditor({
  label,
  value,
  onChange,
  presenceLabel = label,
  inheritHelp = 'Inherited',
  disabled = false,
  readOnly = false,
  disabledReason,
  onBlur,
  onFocus,
}: FactorPolicyEditorProps) {
  const id = useId();
  const scopeId = `${id}-scope`;
  const scope = 'Media rows with standard activities';
  const locked = disabled || readOnly;
  const empty = value.present && value.excludeAutoKind.length === 0;
  const help = disabledReason ?? (!value.present ? inheritHelp : empty ? 'No exclusions. Stops fallback.' : '');

  return (
    <fieldset
      className={styles.field}
      disabled={locked}
      aria-label={`${label} auto factor policy`}
      aria-describedby={`${id}-help`}
      onBlur={onBlur}
      onFocus={onFocus}
    >
      <div className={styles.row}>
        <div className={styles.presence}>
          <Switch
            id={`${id}-present`}
            aria-label={`${label} policy present`}
            size="sm"
            disabled={locked}
            checked={value.present}
            onChange={(event) => onChange({ present: event.target.checked, excludeAutoKind: [] })}
          />
          <label className={styles.presenceLabel} htmlFor={`${id}-present`}>
            {presenceLabel}
          </label>
          <button
            type="button"
            className={styles.scopeIcon}
            aria-label={scope}
            data-tooltip-id={scopeId}
            data-tooltip-content={scope}
          >
            <Icon icon="bolt-auto" color={value.present ? 'danger' : 'secondary'} />
          </button>
        </div>
        {value.present && !disabled && (
          <div className={styles.kinds}>
            {autoFactorKinds.map((kind) => (
              <div key={kind} className={styles.kind}>
                <Checkbox
                  id={`${id}-${kind}`}
                  size="sm"
                  disabled={locked}
                  checked={value.excludeAutoKind.includes(kind)}
                  onChange={(event) => {
                    const kinds = new Set(value.excludeAutoKind);
                    if (event.target.checked) kinds.add(kind);
                    else kinds.delete(kind);
                    onChange({ present: true, excludeAutoKind: autoFactorKinds.filter((item) => kinds.has(item)) });
                  }}
                />
                <label htmlFor={`${id}-${kind}`}>{factorKindLabels[kind]}</label>
              </div>
            ))}
          </div>
        )}
      </div>
      <p
        id={`${id}-help`}
        className={empty && !disabledReason ? styles.empty : styles.help}
        role={empty ? 'status' : undefined}
      >
        {help}
      </p>
      <Tooltip
        id={scopeId}
        positionStrategy="fixed"
        place="top"
        openEvents={{ mouseenter: true, focus: true, click: true }}
        closeEvents={{ mouseleave: true, blur: true }}
        globalCloseEvents={{ escape: true }}
      />
    </fieldset>
  );
}
